import { Button, Grid, InputLabel, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ApiError } from "@readyfit-common/models";
import { handleApiError, handleApiResponse } from "@readyfit-common/utils";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { useAppDispatch } from "src/store/hooks";
import { useUpdatePasswordMutation } from "src/store/reducers/auth/authApiSlice";
import theme from "src/theme";
import useStyles from "./PasswordChangeModal.styles";

interface PasswordChangeModalProps {
  onClose: () => void;
  open: boolean;
  title: string;
  message: string;
  confirmationButtonTitle: string;
}

const PasswordChangeModal = ({
  onClose,
  open,
  title,
  message,
  confirmationButtonTitle,
}: PasswordChangeModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [updatePassword, { isLoading: isUpdatingPassword }] =
    useUpdatePasswordMutation();

  const handleChangePassword = async (values: {
    previousPassword: string;
    proposedPassword: string;
  }) => {
    try {
      const response = await updatePassword({ ...values }).unwrap();
      onClose();
      handleApiResponse(dispatch, response);
    } catch (error) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const formik = useFormik({
    initialValues: {
      previousPassword: "",
      proposedPassword: "",
    },
    validationSchema: Yup.object({
      previousPassword: Yup.string().required("Required"),
      proposedPassword: Yup.string().required("Required"),
    }),
    onSubmit: (values) => handleChangePassword(values),
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={onClose}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Typography variant="h5" fontSize={18}>
          {title}
        </Typography>

        <Typography variant="body1">{message}</Typography>

        <Grid container item xs={12} spacing={2}>
          <Grid item xs={6}>
            <InputLabel className={classes.inputLabel}>
              Current Password
            </InputLabel>
            <TextField
              type="password"
              variant="outlined"
              fullWidth
              className={classes.input}
              value={values.previousPassword}
              onChange={handleChange("previousPassword")}
              error={
                touched.previousPassword && Boolean(errors.previousPassword)
              }
              helperText={touched.previousPassword && errors.previousPassword}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel className={classes.inputLabel}>New Password</InputLabel>
            <TextField
              type="password"
              variant="outlined"
              fullWidth
              className={classes.input}
              value={values.proposedPassword}
              onChange={handleChange("proposedPassword")}
              error={
                touched.proposedPassword && Boolean(errors.proposedPassword)
              }
              helperText={touched.proposedPassword && errors.proposedPassword}
            />
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(2)}
        >
          <Grid item>
            <Button
              color="secondary"
              className={` ${classes.button} ${classes.backButton}`}
              onClick={onClose}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label={confirmationButtonTitle}
              type="submit"
              loading={isUpdatingPassword}
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default PasswordChangeModal;
